/**
 * The single narrow, typed entry point the rest of the portal uses to talk to
 * CloudWatch RUM. Components NEVER import aws-rum-web or call recordEvent
 * directly — they call these functions. Everything here:
 *  - is a safe no-op when RUM is disabled or init failed (never throws to the app);
 *  - routes all metadata through the allowlist sanitizer;
 *  - attaches the breadcrumb trail to error events.
 *
 * Auth model: credentials come from Amplify via Auth.currentCredentials(),
 * which returns GUEST credentials before login and AUTHENTICATED credentials
 * after. Both the Cognito Identity Pool guest role and authenticated role are
 * granted rum:PutRumEvents in IaC (gated on manageRumAppMonitor), so a single
 * RUM session spans pre-login (login/auth errors — the primary debugging
 * surface) and post-login. See docs/observability/telemetry-inventory.md.
 *
 * Function order: leaf/used functions are declared before their callers, and
 * initRum (the orchestrator) is last, to satisfy no-use-before-define.
 */
import { Auth } from 'aws-amplify';
import { AwsRum, type AwsRumConfig } from 'aws-rum-web';

import {
  BreadcrumbBuffer,
  buildApiBreadcrumb,
  buildClickBreadcrumb,
  type ApiBreadcrumbInput,
  type ClickContext,
} from './breadcrumbs';
import { isAllowedEventName, PORTAL_EVENTS, type PortalEventName } from './rumEvents';
import {
  getBackendRegionCode,
  getRumConfig,
  getRumConfigForRegion,
  type RumRuntimeConfig,
} from './rumConfig';
import {
  buildEventMetadata,
  getCommonEventContext,
  setCommonEventContext,
} from './rumMetadata';
import { normalizeRoute } from './routeNormalizer';
import { isReplayRouteSafe } from './replayPolicy';
import { sanitizeSessionAttributes, toSafeError } from './telemetrySanitizer';
import { setWorkflowTimingSink } from './workflowTimer';

let awsRum: AwsRum | null = null;
let initialized = false;
// Installed-once guard for the document/window handlers + workflow sink (they
// survive a monitor switch — see ensureHandlersInstalled).
let handlersInstalled = false;
// The app-monitor id the active client posts to, so reinitRumForRegion can skip
// a no-op switch to the same monitor.
let currentMonitorId: string | null = null;
const breadcrumbs = new BreadcrumbBuffer();
let clickListenerCleanup: (() => void) | null = null;
let supportCorrelationId: string | null = null;

/** True once RUM is live and accepting events. */
export function isRumActive(): boolean {
  return awsRum !== null;
}

/**
 * Whether session replay may be recorded on this route right now: requires the
 * policy gate (non-prod + SESSION_REPLAY_ENABLED flag) AND a replay-safe,
 * non-clinical route. Replay is intentionally NOT enabled in the client today
 * (see initRum); this is the decision function for the controlled evaluation
 * once replay is turned on at the app-monitor level after a security review.
 */
export function shouldRecordReplay(pathname: string): boolean {
  return getRumConfig().sessionReplayEnabled && isReplayRouteSafe(pathname);
}

function mintCorrelationId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to the non-crypto fallback */
  }
  return `c-${Math.random().toString(16).slice(2)}${Math.random().toString(16).slice(2)}`;
}

/**
 * The opaque, random support-correlation id for this app load. No embedded
 * patient/clinic/clinician info — it only links a RUM session to backend
 * logs/audit via authorized internal tooling. Minted lazily so it exists even
 * when RUM is disabled (the backend still logs it for frontend↔backend
 * correlation). Stable for the app load.
 */
export function getSupportCorrelationId(): string {
  if (!supportCorrelationId) {
    supportCorrelationId = mintCorrelationId();
  }
  return supportCorrelationId;
}

/** Set/merge allowlisted session attributes (e.g. clinicianRole post-auth). */
export function setSafeSessionContext(context: Record<string, unknown>): void {
  const safe = sanitizeSessionAttributes(context);
  if (Object.keys(safe).length === 0) {
    return;
  }
  if (typeof safe.backendRegion === 'string') {
    setCommonEventContext({ backendRegion: safe.backendRegion });
  }
  try {
    awsRum?.addSessionAttributes(safe);
  } catch {
    /* no-op */
  }
}

/** Record a manual, normalized page view. Auto page views are disabled. */
export function recordNormalizedPageView(pathname: string): void {
  const route = normalizeRoute(pathname);
  setCommonEventContext({ route: route.pageTemplate, featureArea: route.featureArea });
  try {
    awsRum?.recordPageView({
      pageId: route.pageTemplate,
      pageAttributes: {
        featureArea: route.featureArea,
        screenType: route.screenType,
      },
    });
  } catch {
    /* no-op */
  }
}

/** Emit a catalogued semantic event. Non-catalogued names are dropped. */
export function trackPortalEvent(
  name: PortalEventName | string,
  payload?: Record<string, unknown>,
): void {
  if (!isAllowedEventName(name)) {
    return;
  }
  try {
    awsRum?.recordEvent(name, buildEventMetadata(payload));
  } catch {
    /* no-op */
  }
}

/** Push an API breadcrumb for every request (used by the Axios tap). */
export function recordApiBreadcrumb(input: ApiBreadcrumbInput): void {
  breadcrumbs.push(buildApiBreadcrumb(input, Date.now()));
}

/** Safe, pre-normalized fields describing an API failure. */
export interface ApiFailure {
  apiOperation: string;
  method: string;
  httpStatusGroup: string;
  normalizedErrorCode: string;
  durationBucket?: string;
  isOffline?: boolean;
  cancelled?: boolean;
  backendRegion?: string;
}

/** Emit a portal.api_failed event for a failed request (errors only). */
export function recordApiFailure(failure: ApiFailure): void {
  trackPortalEvent(PORTAL_EVENTS.PORTAL_API_FAILED, { ...failure });
}

function emitErrorEvent(context?: Record<string, unknown>, error?: unknown): void {
  if (!awsRum) {
    return;
  }
  const trail = breadcrumbs.snapshot();
  try {
    if (error !== undefined) {
      // Scrub first: the raw message/stack can carry the rewritten
      // `title: details` backend string (PHI). See toSafeError.
      awsRum.recordError(toSafeError(error));
    }
    awsRum.recordEvent(PORTAL_EVENTS.PORTAL_ERROR, {
      ...buildEventMetadata(context),
      breadcrumbs: trail,
    });
  } catch {
    /* no-op */
  }
}

/**
 * Record a frontend error with the breadcrumb trail attached. Used by
 * RumErrorBoundary for React render errors (which aws-rum-web's built-in
 * 'errors' telemetry does not see). The breadcrumb array is pre-sanitized at
 * capture time, so it is attached to the event data directly (not via the
 * scalar allowlist).
 */
export function recordError(error: unknown, context?: Record<string, unknown>): void {
  emitErrorEvent(context, error);
}

/**
 * Flush the breadcrumb trail for a user-reported problem that produced no JS
 * exception (e.g. "report a problem" / support mode).
 */
export function captureBreadcrumbTrail(reason: string): void {
  emitErrorEvent({ normalizedErrorCode: reason });
}

function getCurrentClickContext(): ClickContext {
  const ctx = getCommonEventContext();
  return {
    route: typeof ctx.route === 'string' ? ctx.route : undefined,
    featureArea: typeof ctx.featureArea === 'string' ? ctx.featureArea : undefined,
  };
}

function installClickBreadcrumbs(): void {
  if (typeof document === 'undefined' || clickListenerCleanup) {
    return;
  }
  const handler = (event: Event) => {
    try {
      const target = event.target as unknown as Parameters<typeof buildClickBreadcrumb>[0];
      breadcrumbs.push(buildClickBreadcrumb(target, getCurrentClickContext(), Date.now()));
    } catch {
      /* never let telemetry break a click */
    }
  };
  document.addEventListener('click', handler, true);
  clickListenerCleanup = () => document.removeEventListener('click', handler, true);
}

function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') {
    return;
  }
  // We do our OWN uncaught-error capture because aws-rum-web's built-in
  // 'errors' telemetry is intentionally disabled (it records the raw error
  // message + stack verbatim, with no scrubbing hook — that's the PHI risk).
  // emitErrorEvent runs the error through toSafeError before recordError, and
  // attaches the breadcrumb trail. `event.error`/`event.reason` may be a raw
  // axios error whose message is `title: details`; toSafeError drops it.
  window.addEventListener('error', (event) => {
    emitErrorEvent({ componentName: 'window.onerror', recoverable: false }, event.error);
  });
  window.addEventListener('unhandledrejection', (event) => {
    emitErrorEvent({ componentName: 'unhandledrejection', recoverable: false }, event.reason);
  });
}

/**
 * Build the aws-rum-web client for `config`, wire the Amplify credential
 * provider, and set the common + session context. Sets the module `awsRum` and
 * `currentMonitorId` on success. Returns false (leaving awsRum null) on any
 * failure — the wrapper degrades to a no-op and never throws to the app.
 */
function startRumClient(config: RumRuntimeConfig): boolean {
  try {
    const rumConfig: AwsRumConfig = {
      sessionSampleRate: config.sessionSampleRate,
      endpoint: config.endpoint,
      // No auto page views (they capture ?patient-id=); no resource URLs (they
      // embed session ids); no X-Ray header injection yet (see plan §7).
      // Session replay is intentionally NOT enabled here — it stays off until a
      // security review turns it on at the app-monitor level; the client gate
      // is shouldRecordReplay() (Phase 6, policy-driven, non-prod + safe routes).
      // 'errors' is deliberately ABSENT: its built-in capture would ship raw
      // error message + stack (potential `title: details` PHI) with no scrub
      // hook. We capture uncaught errors ourselves via installGlobalErrorHandlers
      // → toSafeError instead.
      telemetries: ['performance', 'http'],
      allowCookies: true,
      enableXRay: false,
      disableAutoPageView: true,
      recordResourceUrl: false,
    };
    const client = new AwsRum(
      config.applicationId,
      config.applicationVersion,
      config.region,
      rumConfig,
    );

    // Credentials from Amplify: Auth.currentCredentials() returns GUEST creds
    // pre-login and AUTHENTICATED creds post-login (both roles are granted
    // rum:PutRumEvents in IaC), so pre-login errors are captured too. aws-rum-web
    // takes a credential provider via setAwsCredentials (NOT a config field);
    // it's invoked to refresh creds as needed.
    client.setAwsCredentials(async () => {
      const c = await Auth.currentCredentials();
      return {
        accessKeyId: c.accessKeyId,
        secretAccessKey: c.secretAccessKey,
        sessionToken: c.sessionToken,
      };
    });

    awsRum = client;
    currentMonitorId = config.applicationId;

    const backendRegion = getBackendRegionCode();
    setCommonEventContext({ backendRegion });
    setSafeSessionContext({
      applicationVersion: config.applicationVersion,
      deploymentEnvironment: config.deploymentEnvironment,
      awsRegion: config.region,
      backendRegion,
      releaseChannel: config.releaseChannel,
      supportCorrelationId: getSupportCorrelationId(),
    });
    return true;
  } catch {
    awsRum = null; // any failure → permanent no-op
    return false;
  }
}

/**
 * Install the document/window-level handlers + workflow-timing sink exactly
 * once. They reference the module-level `awsRum`, so after a monitor switch they
 * automatically target the new client — they must NOT be re-installed (that
 * would stack error listeners and double-count clicks).
 */
function ensureHandlersInstalled(): void {
  if (handlersInstalled) {
    return;
  }
  handlersInstalled = true;
  setWorkflowTimingSink((event) =>
    trackPortalEvent(PORTAL_EVENTS.PORTAL_WORKFLOW_TIMING, { ...event }),
  );
  installClickBreadcrumbs();
  installGlobalErrorHandlers();
}

/**
 * Initialize RUM against the DEFAULT (eu-central-1) monitor. Idempotent and
 * crash-proof: marks initialized even on failure so we never retry-storm, and
 * leaves the wrapper as a no-op if anything goes wrong or RUM is disabled (no
 * app monitor id). Post-login, reinitRumForRegion re-points to the clinician's
 * home-region monitor.
 */
export function initRum(): void {
  if (initialized) {
    return;
  }
  initialized = true;
  const config = getRumConfig();
  if (!config.enabled) {
    return;
  }
  if (startRumClient(config)) {
    ensureHandlersInstalled();
  }
}

/**
 * Re-point RUM at the monitor for a clinician's home region. Multi-region auth
 * switches the Cognito identity pool to that region on login, so RUM must post
 * to that region's OWN monitor (the only one that region's credentials are
 * permitted on). No-op when the region maps to no separate monitor (default/eu,
 * unknown region, or single-region mercury) or is already the active monitor.
 * Crash-proof: on any failure it keeps whatever client was active. A fresh RUM
 * session begins at the switch (expected; supportCorrelationId still threads to
 * backend logs). Pre-login/login events already went to the default monitor
 * (region unknown until login) — that split is by design.
 */
export function reinitRumForRegion(homeRegion: string): void {
  const config = getRumConfigForRegion(homeRegion);
  if (!config.enabled) {
    return;
  }
  if (awsRum && config.applicationId === currentMonitorId) {
    return; // already posting to this region's monitor
  }
  // Disable the current client before creating the new one so we don't
  // double-instrument (aws-rum-web patches fetch/XHR for the http telemetry).
  try {
    awsRum?.disable();
  } catch {
    /* no-op */
  }
  awsRum = null;
  if (startRumClient(config)) {
    ensureHandlersInstalled();
  }
}
